import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Button, Row, Col, message, Spin } from 'antd';
import { CheckOutlined, ReloadOutlined, SaveOutlined } from '@ant-design/icons';
import { CardContainer } from '../contract/CardContainer';
import { SectionHeader } from '../contract/SectionHeader';
import { colors, spacing } from '../design-system/tokens';
import { SnippetPreview } from './SnippetPreview';
import { SnippetSelector } from './SnippetSelector';
import { ConflictWarning } from './ConflictWarning';

// Types for the snippet builder
export interface Snippet {
  id: string;
  field: 'shipmentNoteOne' | 'shipmentNoteTwo' | 'packingNotes';
  category: string;
  sourceType: string;
  text: string;
  variations?: SnippetVariation[];
  priority: number;
  required?: boolean;
  metadata?: Record<string, any>;
}

export interface SnippetVariation {
  id: string;
  name: string;
  text: string;
}

export interface SnippetSelection {
  snippetId: string;
  selectedVariationId?: string;
  enabled: boolean;
  userModified?: boolean;
}

export interface ConflictInfo {
  type: 'incompatible' | 'duplicate' | 'missing_required';
  snippetIds: string[];
  description: string;
  suggestion?: string;
}

export interface GeneratedClauses {
  shipmentNoteOne?: string;
  shipmentNoteTwo?: string;
  packingNotes?: string;
}

export interface ShipmentContext {
  shipmentId: string;
  buyer?: { id: string; name: string; code: string };
  supplier?: { id: string; name: string; code: string };
  carrier?: { id: string; name: string; scac: string };
  commodities: Array<{ id: string; name: string; code: string }>;
  containerCount?: number;
}

interface SmartSnippetBuilderProps {
  visible: boolean;
  onClose: () => void;
  shipmentContext: ShipmentContext;
  initialClauses?: GeneratedClauses;
  onSave: (clauses: GeneratedClauses) => void;
}

export const SmartSnippetBuilder: React.FC<SmartSnippetBuilderProps> = ({
  visible,
  onClose,
  shipmentContext,
  initialClauses,
  onSave
}) => {
  // State management
  const [loading, setLoading] = useState(false);
  const [applicableSnippets, setApplicableSnippets] = useState<Map<string, Snippet[]>>(new Map());
  const [selectedSnippets, setSelectedSnippets] = useState<Map<string, SnippetSelection>>(new Map());
  const [conflicts, setConflicts] = useState<ConflictInfo[]>([]);
  const [currentClauses, setCurrentClauses] = useState<GeneratedClauses>({});
  const [isDirty, setIsDirty] = useState(false);

  // Generate clauses when component mounts or context changes
  useEffect(() => {
    if (visible && shipmentContext.shipmentId) {
      generateDefaultClauses();
    }
  }, [visible, shipmentContext]);

  // Update clauses when snippet selections change
  useEffect(() => {
    if (selectedSnippets.size > 0) {
      updateClausePreview();
    }
  }, [selectedSnippets]);

  /**
   * Generate default clauses from rules engine
   */
  const generateDefaultClauses = async () => {
    setLoading(true);
    try {
      // Call the rules engine API to generate clauses
      const response = await fetch(`/api/shipments/${shipmentContext.shipmentId}/generate-clauses`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          context: shipmentContext
        })
      });

      if (!response.ok) {
        throw new Error('Failed to generate clauses');
      }

      const result = await response.json();
      
      // Update state with generated data
      setApplicableSnippets(new Map(Object.entries(result.applicableSnippets)));
      setSelectedSnippets(new Map(Object.entries(result.selectedSnippets)));
      setConflicts(result.conflicts || []);
      setCurrentClauses(result.clauses);
      setIsDirty(false);

      if (result.warnings && result.warnings.length > 0) {
        message.warning(`Generated clauses with ${result.warnings.length} warnings`);
      }
    } catch (error) {
      console.error('Error generating clauses:', error);
      message.error('Failed to generate clauses. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Update clause preview based on current selections
   */
  const updateClausePreview = async () => {
    try {
      const enabledSnippets = Array.from(selectedSnippets.entries())
        .filter(([_, selection]) => selection.enabled)
        .map(([snippetId, selection]) => ({
          snippetId,
          variationId: selection.selectedVariationId
        }));

      const response = await fetch('/api/clauses/preview', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          snippets: enabledSnippets
        })
      });

      if (response.ok) {
        const preview = await response.json();
        setCurrentClauses(preview);
      }
    } catch (error) {
      console.error('Error updating preview:', error);
    }
  };

  /**
   * Handle snippet selection changes
   */
  const handleSnippetChange = (snippetId: string, changes: Partial<SnippetSelection>) => {
    setSelectedSnippets(prev => {
      const updated = new Map(prev);
      const current = updated.get(snippetId) || { 
        snippetId, 
        enabled: false, 
        userModified: false 
      };
      
      updated.set(snippetId, {
        ...current,
        ...changes,
        userModified: true
      });
      
      return updated;
    });
    
    setIsDirty(true);
  };

  /**
   * Reset to default generated clauses
   */
  const handleReset = async () => {
    await generateDefaultClauses();
    message.success('Reset to default clauses');
  };

  /**
   * Save current clauses
   */
  const handleSave = async () => {
    try {
      setLoading(true);
      
      // Validate before saving
      const validation = await validateCurrentSelection();
      if (!validation.valid) {
        message.error(`Cannot save: ${validation.errors.join(', ')}`);
        return;
      }

      // Save the clauses
      await onSave(currentClauses);
      setIsDirty(false);
      message.success('Clauses saved successfully');
      onClose();
    } catch (error) {
      console.error('Error saving clauses:', error);
      message.error('Failed to save clauses');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Validate current selection
   */
  const validateCurrentSelection = async (): Promise<{ valid: boolean; errors: string[] }> => {
    const enabledSnippetIds = Array.from(selectedSnippets.entries())
      .filter(([_, selection]) => selection.enabled)
      .map(([snippetId, _]) => snippetId);

    try {
      const response = await fetch('/api/snippets/validate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          snippetIds: enabledSnippetIds,
          context: shipmentContext
        })
      });

      if (response.ok) {
        const result = await response.json();
        setConflicts(result.conflicts || []);
        return {
          valid: result.isValid,
          errors: result.errors || []
        };
      }
    } catch (error) {
      console.error('Validation error:', error);
    }

    return { valid: false, errors: ['Validation failed'] };
  };

  // Memoized computations
  const fieldOrder = useMemo(() => [
    'shipmentNoteOne',
    'shipmentNoteTwo', 
    'packingNotes'
  ] as const, []);

  const fieldLabels = useMemo(() => ({
    shipmentNoteOne: 'Shipment Note One',
    shipmentNoteTwo: 'Shipment Note Two',
    packingNotes: 'Packing Notes'
  }), []);

  const hasConflicts = conflicts.length > 0;
  const hasRequiredMissing = conflicts.some(c => c.type === 'missing_required');

  return (
    <Modal
      title="Smart Snippet Builder"
      open={visible}
      onCancel={onClose}
      footer={null}
      width="90%"
      style={{ top: 20 }}
      bodyStyle={{ padding: spacing.lg, height: 'calc(90vh - 120px)', overflow: 'hidden' }}
    >
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header Section */}
        <div style={{ marginBottom: spacing.md }}>
          <SectionHeader
            title="Contract Clause Generation"
            subtitle={`${shipmentContext.commodities.map(c => c.name).join(', ')} • ${shipmentContext.buyer?.name || 'Unknown Buyer'}`}
            showBottomBorder={true}
          />
          
          {hasConflicts && (
            <div style={{ marginTop: spacing.md }}>
              <ConflictWarning conflicts={conflicts} />
            </div>
          )}
        </div>

        {/* Main Content */}
        <div style={{ flex: 1, overflow: 'hidden' }}>
          <Row gutter={16} style={{ height: '100%' }}>
            {/* Left Panel - Live Preview */}
            <Col span={12} style={{ height: '100%' }}>
              <CardContainer variant="primary" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <SectionHeader
                  title="📝 Live Preview"
                  subtitle="Real-time clause generation"
                  showBottomBorder={true}
                />
                
                <div style={{ flex: 1, overflow: 'auto', paddingTop: spacing.md }}>
                  <Spin spinning={loading} tip="Generating clauses...">
                    <SnippetPreview
                      clauses={currentClauses}
                      fieldOrder={fieldOrder}
                      fieldLabels={fieldLabels}
                      isDirty={isDirty}
                    />
                  </Spin>
                </div>
              </CardContainer>
            </Col>

            {/* Right Panel - Snippet Builder */}
            <Col span={12} style={{ height: '100%' }}>
              <CardContainer variant="secondary" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <SectionHeader
                  title="🧩 Snippet Builder"
                  subtitle="Select and customize clauses"
                  showBottomBorder={true}
                />
                
                <div style={{ flex: 1, overflow: 'auto', paddingTop: spacing.md }}>
                  <SnippetSelector
                    applicableSnippets={applicableSnippets}
                    selectedSnippets={selectedSnippets}
                    onSnippetChange={handleSnippetChange}
                    shipmentContext={shipmentContext}
                    fieldOrder={fieldOrder}
                    fieldLabels={fieldLabels}
                  />
                </div>
              </CardContainer>
            </Col>
          </Row>
        </div>

        {/* Footer Actions */}
        <div style={{
          marginTop: spacing.md,
          paddingTop: spacing.md,
          borderTop: `1px solid ${colors.border.default}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleReset}
              disabled={loading}
            >
              Reset to Default
            </Button>
          </div>

          <div style={{ display: 'flex', gap: spacing.md }}>
            <Button onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSave}
              loading={loading}
              disabled={hasRequiredMissing}
            >
              Save Clauses
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};